import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

import { trackEvent } from "@/shared/analytics";
import { Button } from "@/shared/ui/button";

import { useReferral } from "../../api/useReferral";
import type { FriendInvite } from "../../model/FriendsBoard";
import { inviteLink } from "../../model/inviteLink";
import { FriendsSection } from "./FriendsSection";
import { SteamAvatar } from "./SteamAvatar";

const PLAYED = msg({
  message: "{count, plural, one {# match} other {# matches}} in the last month",
});
const TOGETHER = msg({ message: "{count} together with you" });

type InvitesSectionProps = {
  invites: readonly FriendInvite[];
  anchorId: string;
};

// «Ещё не с нами»: друзья из Steam без Relock, которые играли за последние 30 дней.
// «Позвать» копирует личную ссылку-приглашение — отправить её другу в Steam или Discord;
// кто пришёл по ней, записывается (`referrals`).
export const InvitesSection = ({ invites, anchorId }: InvitesSectionProps) => {
  const { i18n, t } = useLingui();
  const [copied, setCopied] = useState<ReadonlySet<number>>(new Set());
  const referral = useReferral();
  const invite = (accountId: number) => {
    trackEvent("invite_copy");
    const link = inviteLink(window.location.origin, referral.data?.code ?? null);
    void navigator.clipboard.writeText(link).then(() => {
      setCopied((previous) => new Set(previous).add(accountId));
    });
  };
  return (
    <FriendsSection
      id={anchorId}
      title={<Trans>Not with us yet</Trans>}
      description={
        <Trans>
          Steam friends who played Deadlock in the last month. Invite them to see them in the table.
        </Trans>
      }
    >
      {invites.length === 0 ? (
        <p className="text-sm text-subtle">
          <Trans>All your friends who play Deadlock are already here.</Trans>
        </p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-2.5">
          {invites.map((friend) => {
            const done = copied.has(friend.account_id);
            const parts = [i18n._({ ...PLAYED, values: { count: friend.matches_last_30d } })];
            if (friend.matches_together > 0) {
              parts.push(i18n._({ ...TOGETHER, values: { count: friend.matches_together } }));
            }
            return (
              <li
                key={friend.account_id}
                className="flex items-center justify-between gap-2.5 rounded-[10px] border border-border bg-sidebar px-3 py-2.5"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <SteamAvatar name={friend.display_name} url={friend.avatar_url} />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold">
                      {friend.display_name ?? t`Steam player`}
                    </span>
                    <span className="text-xs text-subtle">{parts.join(" · ")}</span>
                  </span>
                </span>
                <Button
                  variant="outline"
                  className={done ? "min-h-11 text-good" : "min-h-11 text-accent"}
                  onClick={() => {
                    invite(friend.account_id);
                  }}
                >
                  {done ? <Trans>Link copied</Trans> : <Trans>Invite</Trans>}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[13px] text-subtle">
        <Trans>
          “Invite” copies a link to Relock — send it to your friend on Steam or Discord.
        </Trans>
      </p>
    </FriendsSection>
  );
};
